import type { ReactElement, ReactNode, RefObject } from "react";
import { MAX_FIRST_NAME_CHARS, MIN_FIRST_NAME_CHARS, pixelType, type SPACING } from "odyssey-shared";
import { Button, EmptyState, Glyph, KIT_METRICS, List, ListAction, Panel, TextField, TextLink, listRowHeight, logical, type ListEntry } from "../ui/kit/index.ts";
import { say } from "../words.ts";
import type { Question } from "./ask.ts";
import { EntryScreen } from "./entry-screen.tsx";
import { NAME_PART_FIELD_PATTERN } from "./login-screen.tsx";
import type { LazyImages } from "./look-preview/images.ts";
import type { LookPreviewSource } from "./look-preview/look-figure.tsx";
import { StandingLook, standingStage } from "./look-preview/standing-look.tsx";
import { characterLabel, chosenCharacter, slotRow, type SelectNote, type SelectState } from "./select-state.ts";

/**
 * Character select on the kit (P1.4-T6, P1.4-T28 and P1.9-T61, ADR-065 decisions 5 and 6; ADR-061's amendment
 * "character select as redesigned", the design a visual reference): in the layout the entry screens share, a window
 * 240 wide. At its top the account row: the account's family name under its label on the left, or no text when the
 * account has none, and Log out, a danger button that asks first, on the right, away from Play. Then the account's
 * characters in creation order by their full names ("Kaelen Ashgrove"), never the account's email, with the first
 * selected, each after its standing look (ADR-094 decision 27, `standing-look.tsx`), or the standing figure glyph
 * while the look sheets load, when they did not load and for a character the list names no look for, in a panel;
 * while the account holds fewer characters than its limit, New character, a button drawn as the panel's last row,
 * whose sub-line counts the slots and which opens the create step in the same window (Back returns to the list); at
 * the limit a plain line in its place. Under that Change look for the selected character (decision 29, free and
 * always there while the change of look is free, ADR-094's amendment B3), which opens the Look panel with its voice
 * (note 128). The voice is chosen there and on the creation's Look step, not on the list (note 125). Under it a note
 * line holds the busy words or the answer's sentence, read out as it appears, and takes no height while it is
 * empty. Play, the window's one footer button and the screen's one primary, plays
 * the selected character, as Enter on a row does. Log out everywhere is a link under the window. The list scrolls
 * past `SELECT_METRICS.visibleRows` rows, by whole rows.
 */

/**
 * The question Log out asks before it leaves character select: its words (`account.select.logOutTitle` and
 * `logOutBody`, the design's of 2026-10-01), the entry screens' Log out on its button, the danger tone and its
 * width in logical pixels.
 *
 * @returns The question.
 */
export function logOutQuestion(): Question {
  return { title: say("account.select.logOutTitle"), body: say("account.select.logOutBody"), confirm: say("account.naming.switch"), tone: "danger", width: 200 };
}

/**
 * A spacing step of the token table, checked at compile time.
 *
 * @param step One of `SPACING`.
 * @returns The step in logical pixels.
 */
function space(step: (typeof SPACING)[number]): number {
  return step;
}

/**
 * The screen's measurements in logical pixels: the window's width, as the design draws it; the gap between the
 * window's parts and between the links, as the login form's; the account row's label and name each on a line as tall
 * as the label face's measured size, 1 apart, so one starts 12 below the other as the design's 10 on 10 lines 2 apart
 * do; a link's line; the rows the list shows before it scrolls, which holds the default limit of three and
 * the future premium tier's five whole (ADR-065 decision 5); a row's height, the list's row with its 12 px glyph;
 * and Play's least width, as the design draws it. A row that leads with a standing look grows to hold it, and the
 * list then shows `figureRows` before it scrolls, so the window still fits the phone floor of 445 by 230.
 */
export const SELECT_METRICS = {
  window: 240,
  play: 80,
  gap: space(4),
  linkGap: space(4),
  accountGap: 1,
  accountLine: pixelType("label").size,
  link: pixelType("label").lineHeight,
  visibleRows: 5,
  figureRows: 3,
  row: listRowHeight({ glyph: true, sub: false, trailing: false }),
} as const;

/**
 * What the list draws each character's standing look from: the preview's source and one image store for every row,
 * with the count of the images that have arrived.
 */
export interface SelectFigures {
  readonly source: LookPreviewSource;
  readonly images: LazyImages;
  readonly arrivals: number;
}

/**
 * What the screen draws and whom it tells: the state, a ref to the new character's first-name input, which
 * the page focuses as the state asks, what the rows draw their standing looks from (null while the look sheets
 * load and when they did not load), and the player's actions. `onEnter` is given the character to play, the
 * selected one or the row Enter acted on.
 */
export interface SelectScreenProps {
  readonly state: SelectState;
  readonly firstName: RefObject<HTMLInputElement | null>;
  readonly figures?: SelectFigures | null;
  readonly onChoose: (id: string) => void;
  readonly onEnter: (id: string | null) => void;
  readonly onCreate: () => void;
  readonly onChangeLook: () => void;
  readonly onType: (value: string) => void;
  readonly onSubmitName: () => void;
  readonly onBack: () => void;
  readonly onLogOut: () => void;
  readonly onLogOutEverywhere: () => void;
}

/**
 * The list's height: the listbox's own for as many rows as it shows whole, and the scrolling height of
 * `SELECT_METRICS.visibleRows` rows past that, or `figureRows` rows of a list whose rows lead with standing looks.
 *
 * @param rows How many characters the list holds.
 * @param figure The height of a standing look a row leads with, or null when the rows lead with the glyph.
 * @returns The height in logical pixels, or undefined when the rows set it.
 */
export function listHeight(rows: number, figure: number | null = null): number | undefined {
  const shown = figure === null ? SELECT_METRICS.visibleRows : SELECT_METRICS.figureRows;
  const row = figure === null ? SELECT_METRICS.row : listRowHeight({ glyph: true, sub: false, trailing: false, lead: figure });
  return rows > shown ? shown * row : undefined;
}

/**
 * The note line under the list: the busy words as a hint or a refusal as an error led by the 8 px alert glyph, in
 * the caption of a field's note line and as tall as one. With neither it stays on the page, empty, taking no height
 * and pulling back the gap before it, so the list sits directly above the footer as the design draws it (the lead's
 * ruling under "match the design"). It is a polite live region, so a sentence that appears is read out.
 *
 * @param props The note, or null.
 * @returns The line.
 */
function NoteLine({ note }: { readonly note: SelectNote | null }): ReactElement {
  const failed = note?.tone === "error";
  return (
    <div className={failed ? "ot-select__note is-error" : "ot-select__note"} data-select="note" aria-live="polite" style={note === null ? { height: 0, marginTop: logical(-SELECT_METRICS.gap) } : { minHeight: logical(KIT_METRICS.note.height), gap: logical(KIT_METRICS.note.gap) }}>
      {failed ? (
        <span className="ot-field__mark" style={{ marginTop: logical(KIT_METRICS.note.glyphOffset) }}>
          <Glyph name="alert" />
        </span>
      ) : null}
      {note === null ? null : (
        <span className="ot-select__note-text" style={{ marginTop: logical(KIT_METRICS.note.textOffset) }}>
          {note.text}
        </span>
      )}
    </div>
  );
}

/**
 * A row of links a link's line tall.
 *
 * @param props The links.
 * @returns The row.
 */
function Links({ children }: { readonly children: ReactNode }): ReactElement {
  return (
    <div className="ot-login__links" data-login="links" style={{ height: logical(SELECT_METRICS.link), gap: logical(SELECT_METRICS.linkGap) }}>
      {children}
    </div>
  );
}

/**
 * The rows the list shows: each character by its full name after its standing look, or after the standing figure
 * glyph without one.
 *
 * @param state The screen's state.
 * @param figures What the standing looks draw from, or null.
 * @returns The rows, in creation order.
 */
export function selectRows(state: SelectState, figures: SelectFigures | null = null): ListEntry<string>[] {
  const stage = standingStage(figures?.source ?? null);
  return state.characters.map((character) => {
    const label = characterLabel(character, state.familyName);
    const look = character.look;
    if (figures === null || stage === null || look === undefined) {
      return { value: character.id, label, glyph: "human" };
    }
    return { value: character.id, label, glyph: "human", lead: { height: stage.height, node: <StandingLook source={figures.source} images={figures.images} arrivals={figures.arrivals} look={look} label={label} /> } };
  });
}

/**
 * The account row: the family name under its label on the left, or no text with no family name, and Log out on the
 * right.
 *
 * @param props The account's `familyName`, empty for none, and `onLogOut`.
 * @returns The row.
 */
function AccountRow({ familyName, onLogOut }: { readonly familyName: string; readonly onLogOut: () => void }): ReactElement {
  return (
    <div className="ot-select__account" data-select="account">
      {familyName === "" ? null : (
        <div className="ot-select__who" style={{ gap: logical(SELECT_METRICS.accountGap), lineHeight: logical(SELECT_METRICS.accountLine) }}>
          <span className="ot-select__label">{say("account.select.account")}</span>
          <span className="ot-select__name" data-select="family">
            {familyName}
          </span>
        </div>
      )}
      <span className="ot-select__leave">
        <Button variant="danger" icon="logout" onClick={onLogOut}>
          {say("account.naming.switch")}
        </Button>
      </span>
    </div>
  );
}

/**
 * The account row, the list of the account's characters in its panel with New character as its last row while
 * there is room, the line at the limit, Change look for the selected character, and the note line.
 *
 * @param props The screen's props.
 * @returns The window's content.
 */
function Choose({ state, figures = null, onChoose, onEnter, onCreate, onChangeLook, onLogOut }: SelectScreenProps): ReactElement {
  const items = selectRows(state, figures);
  const chosen = chosenCharacter(state);
  const leads = items.some((item) => item.lead !== undefined);
  const height = listHeight(items.length, leads ? (standingStage(figures?.source ?? null)?.height ?? null) : null);
  const slots = slotRow(state);
  const list = <List label={say("account.select.list")} items={items} value={state.selected} onChange={onChoose} onActivate={(id) => onEnter(id)} empty={<EmptyState>{say("account.select.empty")}</EmptyState>} {...(height === undefined ? {} : { height })} />;
  return (
    <div className="ot-login__form" data-select="form" style={{ gap: logical(SELECT_METRICS.gap) }}>
      <AccountRow familyName={state.familyName} onLogOut={onLogOut} />
      <Panel padding={0}>
        {list}
        {slots?.kind === "room" ? <ListAction label={say("account.select.newCharacter")} glyph="user-plus" sub={say("account.select.slots", { used: String(slots.used), limit: String(slots.limit) })} onPress={onCreate} /> : null}
      </Panel>
      {slots?.kind === "full" ? (
        <p className="ot-select__full" data-select="full">
          {say("account.select.full", { limit: String(slots.limit) })}
        </p>
      ) : null}
      {chosen === null ? null : (
        <span className="ot-select__change" data-select="change-look">
          <Button icon="human" onClick={onChangeLook} disabled={state.busy}>
            {say("look.change.item")}
          </Button>
        </span>
      )}
      <NoteLine note={state.note} />
    </div>
  );
}

/**
 * Play, the window's one footer button: the primary, with the play glyph, at least `SELECT_METRICS.play` wide, busy
 * while a request is out.
 *
 * @param props The screen's props.
 * @returns The button.
 */
function Play({ state, onEnter }: SelectScreenProps): ReactElement {
  return (
    <span className="ot-select__play" data-select="play" style={{ minWidth: logical(SELECT_METRICS.play) }}>
      <Button variant="primary" icon="play" fullWidth busy={state.busy} disabled={state.selected === null} onClick={() => onEnter(state.selected)}>
        {say("account.select.play")}
      </Button>
    </span>
  );
}

/**
 * The create step: the new character's first name, as the name step's first-name field asks for it, with the
 * note under it holding a refusal, Continue to the Look step, and Back.
 *
 * @param props The screen's props.
 * @returns The window's content.
 */
function Create({ state, firstName, onType, onSubmitName, onBack }: SelectScreenProps): ReactElement {
  const note = state.note === null ? { hint: say("account.namingNote") } : state.note.tone === "error" ? { error: state.note.text } : { hint: state.note.text };
  return (
    <form
      className="ot-login__form"
      data-select="create"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        onSubmitName();
      }}
      style={{ gap: logical(SELECT_METRICS.gap) }}
    >
      <TextField label={say("account.labels.firstName")} value={state.firstName} onChange={onType} name="first-name" autoComplete="off" required minLength={MIN_FIRST_NAME_CHARS} maxLength={MAX_FIRST_NAME_CHARS} counter pattern={NAME_PART_FIELD_PATTERN} inputRef={firstName} {...note} />
      <div className="ot-login__actions">
        <Button variant="primary" type="submit" busy={state.busy}>
          {say("account.naming.next")}
        </Button>
      </div>
      <Links>
        <TextLink onClick={onBack}>{say("account.select.back")}</TextLink>
      </Links>
    </form>
  );
}

/**
 * Character select: the entry layout's window titled for the list or for the create step, holding it; while it
 * lists the characters, Play in its footer and Log out everywhere under it.
 *
 * @param props The state, the first name's ref and the player's actions.
 * @returns The screen.
 */
export function SelectScreen(props: SelectScreenProps): ReactElement {
  const { state } = props;
  const creating = state.mode === "create";
  const slots = slotRow(state);
  const below = creating ? undefined : <TextLink onClick={props.onLogOutEverywhere}>{say("account.everywhere")}</TextLink>;
  return (
    <EntryScreen
      mode={creating ? "create" : "select"}
      layout={`${state.mode}:${state.characters.length}:${slots?.kind ?? "none"}:${state.familyName === "" ? "nameless" : "named"}`}
      title={creating ? say("account.naming.title") : say("account.select.title")}
      width={SELECT_METRICS.window}
      {...(creating ? {} : { footer: <Play {...props} />, below })}
    >
      {creating ? <Create {...props} /> : <Choose {...props} />}
    </EntryScreen>
  );
}
